import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Plus, CheckCircle, XCircle, Edit2, Trash2, X, Layers, Search } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

// ── Auth header ───────────────────────────────────────────────────
const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const Schema = Yup.object({
  employee_id:   Yup.number().required("Required"),
  leave_type_id: Yup.number().required("Required"),
  from_date:     Yup.string().required("Required"),
  to_date:       Yup.string().required("Required"),
  reason:        Yup.string().required("Required"),
});

const TABS = [
  { key:"all",      label:"All"      },
  { key:"Pending",  label:"Pending"  },
  { key:"Approved", label:"Approved" },
  { key:"Rejected", label:"Rejected" },
];

const EMPTY_LT  = { name:"", days_per_year:0, carry_forward:false, max_carry:0, color:"#00C8FF", description:"" };

// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts.
// Works off whatever list is passed in (already filtered/searched) —
// caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

export default function LeaveRequests() {
  const { T } = useTheme();
  const TAB_COLOR = { all:T.cyan, Pending:T.amber, Approved:T.green, Rejected:T.red };

  const [requests,   setRequests]   = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState("all");
  const [reqOpen,    setReqOpen]    = useState(false);
  const [ltOpen,     setLtOpen]     = useState(false);
  const [ltMode,     setLtMode]     = useState("add");
  const [ltData,     setLtData]     = useState(EMPTY_LT);
  const [ltEditId,   setLtEditId]   = useState(null);
  const [rejectOpen,   setRejectOpen]   = useState(false);
  const [rejectId,     setRejectId]     = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reqDelOpen, setReqDelOpen] = useState(false);
  const [reqDelId,   setReqDelId]   = useState(null);
  const [ltDelOpen,  setLtDelOpen]  = useState(false);
  const [ltDelId,    setLtDelId]    = useState(null);

  // ── Leave Requests — search, month filter & pagination ──────────
  const [search,      setSearch]      = useState("");
  const [monthFilter, setMonthFilter] = useState("");   // "YYYY-MM" or ""
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 20;

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rRes, ltRes, eRes] = await Promise.all([
        axios.get(APIConstant.leaveRequests,  ax()),
        axios.get(APIConstant.leaveTypes,     ax()),
        axios.get(APIConstant.employees,      ax()),
      ]);
      setRequests(rRes.data   || []);
      setLeaveTypes(ltRes.data || []);
      setEmployees(eRes.data  || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Tab filter (unchanged) + search (employee name/code) + month
  // (matches the leave's start date, backend may use start_date or from_date)
  const filtered = useMemo(() => requests.filter(r => {
    const matchTab = activeTab === "all" || r.status === activeTab;
    if (!matchTab) return false;

    const q = search.toLowerCase();
    const matchSearch = !q || `${r.employee_name || ""} ${r.employee_code || ""}`.toLowerCase().includes(q);
    if (!matchSearch) return false;

    if (monthFilter) {
      const dateStr = r.start_date || r.from_date || "";
      if (!dateStr.startsWith(monthFilter)) return false;
    }
    return true;
  }), [requests, activeTab, search, monthFilter]);

  const counts = useMemo(() => {
    const c = { all:requests.length, Pending:0, Approved:0, Rejected:0 };
    requests.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [requests]);

  // Reset to page 1 whenever tab, search, or month filter changes
  useEffect(() => { setPage(1); }, [activeTab, search, monthFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // ── Actions ───────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try { await axios.put(`${APIConstant.leaveRequests}${id}/approve`, {}, ax()); fetchAll(); }
    catch(e) { alert(e.response?.data?.detail || "Error approving"); }
  };

  const handleReject = async () => {
    try {
      await axios.put(
        `${APIConstant.leaveRequests}${rejectId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {}, ax()
      );
      setRejectOpen(false); fetchAll();
    } catch(e) { alert(e.response?.data?.detail || "Error rejecting"); }
  };

  const handleDeleteReq = async () => {
    try { await axios.delete(`${APIConstant.leaveRequests}${reqDelId}`, ax()); setReqDelOpen(false); fetchAll(); }
    catch(e) { console.error(e); }
  };

  const handleSubmitReq = async (vals) => {
    try {
      // HR creates on behalf of employee — uses hr-create endpoint
      const params = new URLSearchParams({
        employee_id:   vals.employee_id,
        leave_type_id: vals.leave_type_id,
        from_date:     vals.from_date,
        to_date:       vals.to_date,
        reason:        vals.reason,
        days:          vals.days || 1,
      });
      await axios.post(`${APIConstant.leaveRequests}hr-create?${params}`, {}, ax());
      setReqOpen(false); fetchAll();
    } catch(e) { alert(e.response?.data?.detail || "Error creating request"); }
  };

  // ── Leave Type CRUD ───────────────────────────────────────────
  const openAddLt  = () => { setLtMode("add"); setLtEditId(null); setLtData(EMPTY_LT); setLtOpen(true); };
  const openEditLt = (lt) => {
    setLtMode("edit"); setLtEditId(lt.id);
    setLtData({
      name:          lt.name,
      days_per_year: lt.days_per_year || lt.default_days || 0,
      carry_forward: lt.carry_forward,
      max_carry:     lt.max_carry || 0,
      color:         lt.color || "#00C8FF",
      description:   lt.description || "",
    });
    setLtOpen(true);
  };

  const saveLt = async () => {
    if (!ltData.name.trim()) return;
    try {
      const payload = {
        ...ltData,
        default_days: ltData.days_per_year,  // send both for compatibility
      };
      if (ltMode === "edit" && ltEditId) {
        await axios.put(`${APIConstant.leaveTypes}${ltEditId}`, payload, ax());
      } else {
        await axios.post(APIConstant.leaveTypes, payload, ax());
      }
      setLtOpen(false); fetchAll();
    } catch(e) { console.error(e); }
  };

  const deleteLt = async () => {
    try { await axios.delete(`${APIConstant.leaveTypes}${ltDelId}`, ax()); setLtDelOpen(false); fetchAll(); }
    catch(e) { console.error(e); }
  };

  const getBadge = (status) => {
    const map = { Pending:styles.amber, Approved:styles.green, Rejected:styles.red, Cancelled:styles.grey };
    return <span className={`${styles.badge} ${map[status] || styles.grey}`}>{status}</span>;
  };

  const inputStyle = (err) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err ? T.red : T.border}`,
    borderRadius:8, color:T.text, fontSize:13,
    padding:"9px 12px", outline:"none",
    fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });

  const labelStyle = {
    fontSize:11.5, color:T.muted, display:"block",
    marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em",
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>TIME & ATTENDANCE</div>
          <h2>Leave Requests</h2>
          <p>Manage employee leave applications and policies</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={openAddLt}>
            <Layers size={14}/> Manage Leave Types
          </button>
          <button className={styles.btnPrimary} onClick={() => setReqOpen(true)}>
            <Plus size={14}/> New Request
          </button>
        </div>
      </div>

      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Total Requests",   val:counts.all,      color:"cyan"  },
          { label:"Pending Approval", val:counts.Pending,  color:"amber" },
          { label:"Approved",         val:counts.Approved, color:"green" },
          { label:"Rejected",         val:counts.Rejected, color:"red"   },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.card} style={{ marginBottom:20 }}>
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}` }}>
          {TABS.map(({ key, label }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{
                padding:"12px 18px", fontSize:12.5, fontWeight:500,
                fontFamily:"Outfit,sans-serif",
                color: activeTab === key ? TAB_COLOR[key] : T.muted,
                background:"none", border:"none",
                borderBottom: activeTab === key ? `2px solid ${TAB_COLOR[key]}` : "2px solid transparent",
                cursor:"pointer", display:"flex", alignItems:"center", gap:7,
              }}>
              {label}
              <span style={{ fontSize:10.5, padding:"1px 7px", borderRadius:10, background:T.toggle_bg, fontFamily:"JetBrains Mono,monospace" }}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {/* Search + month filter */}
        <div style={{ display:"flex", gap:10, padding:"14px 22px",
          borderBottom:`1px solid ${T.border}`, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:200 }}>
            <Search size={13} style={{ position:"absolute", left:10, top:"50%",
              transform:"translateY(-50%)", color:T.dim }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee…"
              style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                borderRadius:8, color:T.text, fontSize:13, padding:"7px 12px 7px 30px",
                outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
          </div>
          <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            style={{ background:T.inp_bg, border:`1px solid ${T.border}`,
              borderRadius:8, color:T.text, fontSize:13, padding:"7px 12px",
              outline:"none", fontFamily:"Outfit,sans-serif" }}/>
          {monthFilter && (
            <button onClick={() => setMonthFilter("")}
              style={{ padding:"7px 12px", borderRadius:8, border:`1px solid ${T.border}`,
                background:"none", color:T.muted, cursor:"pointer",
                fontFamily:"Outfit,sans-serif", fontSize:12.5 }}>
              Clear month
            </button>
          )}
        </div>

        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr><th>Employee</th><th>Department</th><th>Leave Type</th><th>From</th><th>To</th><th>Days</th><th>LOP</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={10}>No {activeTab === "all" ? "" : activeTab.toLowerCase()} requests found</td></tr>
                ) : paginated.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight:500 }}>{r.employee_name || "—"}</div>
                      <div style={{ fontSize:11, color:T.muted }}>{r.employee_code}</div>
                    </td>
                    <td className={styles.dim}>{r.department || "—"}</td>
                    <td>
                      {/* backend returns r.leave_type and r.leave_color */}
                      <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20,
                        background:`${r.leave_color || r.leave_type_color || T.cyan}20`,
                        color: r.leave_color || r.leave_type_color || T.cyan, fontWeight:600 }}>
                        {r.leave_type || r.leave_type_name || "—"}
                      </span>
                    </td>
                    <td className={styles.dim}>{r.start_date || r.from_date}</td>
                    <td className={styles.dim}>{r.end_date   || r.to_date}</td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan, fontWeight:700 }}>{r.days}</span></td>
                    <td>
                      {r.is_lop
                        ? <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${T.red}1A`, color:T.red, fontWeight:600 }}>LOP</span>
                        : <span style={{ color:T.dim, fontSize:12 }}>—</span>}
                    </td>
                    <td>
                      <div style={{ maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:12.5, color:T.muted }} title={r.reason}>
                        {r.reason || "—"}
                      </div>
                    </td>
                    <td>{getBadge(r.status)}</td>
                    <td>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        {r.status === "Pending" && (
                          <>
                            <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }}
                              onClick={() => handleApprove(r.id)}>
                              <CheckCircle size={12}/> Approve
                            </button>
                            <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }}
                              onClick={() => { setRejectId(r.id); setRejectReason(""); setRejectOpen(true); }}>
                              <XCircle size={12}/> Reject
                            </button>
                          </>
                        )}
                        <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}33` }}
                          onClick={() => { setReqDelId(r.id); setReqDelOpen(true); }}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                      {(r.remarks || r.rejection_reason) && (
                        <div style={{ fontSize:11, color:T.red, marginTop:4 }}>↳ {r.remarks || r.rejection_reason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            T={T}
          />
        )}
      </div>

      {/* Leave Types section — unchanged (no search/pagination requested) */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h3>Leave Types & Policies</h3>
            <p style={{ margin:"4px 0 0", fontSize:12, color:T.muted }}>Click edit to modify days, carry-forward rules and colour</p>
          </div>
          <button className={styles.btnPrimary} onClick={openAddLt}><Plus size={14}/> Add Type</button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.darkTable}>
            <thead>
              <tr><th>Leave Type</th><th>Days / Year</th><th>Carry Forward</th><th>Max Carry</th><th>Description</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {leaveTypes.length === 0 ? (
                <tr className={styles.emptyRow}><td colSpan={6}>No leave types configured</td></tr>
              ) : leaveTypes.map(lt => (
                <tr key={lt.id}>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                      <div style={{ width:12, height:12, borderRadius:3, background:lt.color, flexShrink:0 }}/>
                      <span style={{ fontWeight:500 }}>{lt.name}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan, fontWeight:700, fontSize:14 }}>
                      {lt.default_days || lt.days_per_year || 0}
                    </span>
                  </td>
                  <td><span className={`${styles.badge} ${lt.carry_forward ? styles.green : styles.grey}`}>{lt.carry_forward ? "Yes" : "No"}</span></td>
                  <td className={styles.dim}>{lt.max_carry || "—"}</td>
                  <td className={styles.dim} style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lt.description || "—"}</td>
                  <td>
                    <div style={{ display:"flex", gap:6 }}>
                      <button className={styles.btnSm} onClick={() => openEditLt(lt)}><Edit2 size={12}/> Edit</button>
                      <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }}
                        onClick={() => { setLtDelId(lt.id); setLtDelOpen(true); }}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}

      {/* New Leave Request */}
      <Dialog open={reqOpen} onClose={() => setReqOpen(false)} maxWidth="sm">
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:500, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>New Leave Request</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setReqOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24 }}>
            <Formik
              initialValues={{ employee_id:"", leave_type_id:"", from_date:"", to_date:"", days:1, reason:"" }}
              validationSchema={Schema}
              onSubmit={handleSubmitReq}
            >
              {({ values, errors, touched, handleChange, setFieldValue }) => {
                const calcDays = (f, t) => {
                  if (!f || !t) return 1;
                  let days = 0, cur = new Date(f);
                  const end = new Date(t);
                  while (cur <= end) { if (cur.getDay()!==0 && cur.getDay()!==6) days++; cur.setDate(cur.getDate()+1); }
                  return Math.max(1, days);
                };
                return (
                  <Form style={{ display:"flex", flexDirection:"column", gap:14 }}>
                    <div>
                      <label style={labelStyle}>Employee *</label>
                      <Field as="select" name="employee_id" style={inputStyle(errors.employee_id && touched.employee_id)}>
                        <option value="">Select employee</option>
                        {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                      </Field>
                    </div>
                    <div>
                      <label style={labelStyle}>Leave Type *</label>
                      <Field as="select" name="leave_type_id" style={inputStyle(errors.leave_type_id && touched.leave_type_id)}>
                        <option value="">Select type</option>
                        {leaveTypes.map(lt => (
                          <option key={lt.id} value={lt.id}>
                            {lt.name} ({lt.default_days || lt.days_per_year || 0} days/yr)
                          </option>
                        ))}
                      </Field>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      {["from_date","to_date"].map(n => (
                        <div key={n}>
                          <label style={labelStyle}>{n === "from_date" ? "From *" : "To *"}</label>
                          <Field name={n} type="date"
                            onChange={e => {
                              handleChange(e);
                              const f = n==="from_date" ? e.target.value : values.from_date;
                              const t = n==="to_date"   ? e.target.value : values.to_date;
                              setFieldValue("days", calcDays(f, t));
                            }}
                            style={inputStyle(errors[n] && touched[n])}/>
                        </div>
                      ))}
                    </div>
                    <div style={{ background:`${T.cyan}0F`, border:`1px solid ${T.cyan}1F`, borderRadius:8, padding:"9px 14px", fontSize:13, color:T.cyan }}>
                      📅 Duration: <strong>{values.days} working day{values.days !== 1 ? "s" : ""}</strong>
                    </div>
                    <div>
                      <label style={labelStyle}>Reason *</label>
                      <Field as="textarea" name="reason" rows={3} placeholder="Reason for leave"
                        style={{ ...inputStyle(errors.reason && touched.reason), resize:"vertical" }}/>
                    </div>
                    <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                      <button type="button" onClick={() => setReqOpen(false)} className={styles.btnSecondary}>Cancel</button>
                      <button type="submit" className={styles.btnPrimary}>Submit Request</button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <div style={{ background:T.card, border:`1px solid ${T.red}4D`, borderRadius:14, width:420, padding:24, fontFamily:"Outfit,sans-serif" }}>
          <h3 style={{ fontSize:16, fontWeight:600, color:T.text, margin:"0 0 14px" }}>Reject Leave Request</h3>
          <label style={labelStyle}>Rejection Reason</label>
          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
            placeholder="Provide a reason…"
            style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.red}33`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", resize:"vertical", boxSizing:"border-box" }}/>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:14 }}>
            <button onClick={() => setRejectOpen(false)} className={styles.btnSecondary}>Cancel</button>
            <button onClick={handleReject} style={{ display:"inline-flex", alignItems:"center", gap:7, background:`${T.red}26`, border:`1px solid ${T.red}4D`, borderRadius:8, color:T.red, fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600, padding:"9px 18px", cursor:"pointer" }}>
              <XCircle size={14}/> Reject
            </button>
          </div>
        </div>
      </Dialog>

      {/* Add/Edit Leave Type */}
      <Dialog open={ltOpen} onClose={() => setLtOpen(false)}>
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:460, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>{ltMode === "edit" ? "Edit Leave Type" : "Add Leave Type"}</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setLtOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={labelStyle}>Leave Type Name *</label>
              <input value={ltData.name} onChange={e => setLtData({...ltData,name:e.target.value})} placeholder="e.g. Earned Leave" style={inputStyle(!ltData.name.trim())}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={labelStyle}>Days Per Year</label>
                <input type="number" min={0} value={ltData.days_per_year}
                  onChange={e => setLtData({...ltData, days_per_year:parseInt(e.target.value)||0})} style={inputStyle(false)}/>
              </div>
              <div>
                <label style={labelStyle}>Max Carry Forward</label>
                <input type="number" min={0} value={ltData.max_carry} disabled={!ltData.carry_forward}
                  onChange={e => setLtData({...ltData, max_carry:parseInt(e.target.value)||0})}
                  style={{...inputStyle(false), opacity:ltData.carry_forward?1:0.4}}/>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={labelStyle}>Carry Forward</label>
                <select value={String(ltData.carry_forward)}
                  onChange={e => setLtData({...ltData, carry_forward:e.target.value==="true", max_carry:e.target.value==="false"?0:ltData.max_carry})}
                  style={inputStyle(false)}>
                  <option value="false">No</option><option value="true">Yes</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Colour</label>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <input type="color" value={ltData.color} onChange={e => setLtData({...ltData,color:e.target.value})}
                    style={{ width:40, height:38, background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, cursor:"pointer", padding:2 }}/>
                  <div style={{ flex:1, background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, padding:"9px 12px", fontSize:13, fontFamily:"JetBrains Mono,monospace", color:ltData.color }}>{ltData.color}</div>
                </div>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Colour Presets</label>
              <div style={{ display:"flex", gap:8 }}>
                {["#00C8FF","#00E5A3","#FFB800","#FF4D6D","#7C5CFF","#3B82F6","#A855F7","#7A93B4"].map(c => (
                  <button key={c} onClick={() => setLtData({...ltData,color:c})}
                    style={{ width:28, height:28, borderRadius:6, background:c, border:ltData.color===c?"2px solid #fff":"2px solid transparent", cursor:"pointer" }}/>
                ))}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <textarea value={ltData.description} rows={2} onChange={e => setLtData({...ltData,description:e.target.value})}
                placeholder="Optional description" style={{...inputStyle(false), resize:"vertical"}}/>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
              <button onClick={() => setLtOpen(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={saveLt} className={styles.btnPrimary}>{ltMode === "edit" ? "Update Type" : "Save Type"}</button>
            </div>
          </div>
        </div>
      </Dialog>

      <Popup open={reqDelOpen} message="Delete this leave request?" handleClose={() => setReqDelOpen(false)} handleConfirm={handleDeleteReq}/>
      <Popup open={ltDelOpen}  message="Delete this leave type? All related balances will be affected." handleClose={() => setLtDelOpen(false)} handleConfirm={deleteLt}/>
    </div>
  );
}